import {OS} from '@github-ui/get-os'
import type {GenericAsset} from '../../../../../brand/lib/types/contentful'

// Matches Primer Brand's `isLarge` breakpoint.
export const DESKTOP_MEDIA_QUERY = '(min-width: 1012px)'

export type TabMedia = {
  /** Shown by default, and on small screens. */
  fallback: GenericAsset
  /** Shown instead of `fallback` when `DESKTOP_MEDIA_QUERY` matches. */
  desktop?: GenericAsset
  /** Images shown while a video loads, picked with the same rules as the video. */
  poster?: {fallback: GenericAsset; desktop?: GenericAsset}
}

const hasFile = (item: GenericAsset) => Boolean(item.fields.asset.fields.file)

const contentTypeOf = (item: GenericAsset) => item.fields.asset.fields.file?.contentType ?? ''
export const isVideo = (item: GenericAsset) => contentTypeOf(item).startsWith('video/')
export const isImage = (item: GenericAsset) => contentTypeOf(item).startsWith('image/')

/**
 * Picks the tab's assets by their Contentful `id`:
 *
 * - `macosMobile`    -> iOS, and macOS by default
 * - `macosDesktop`   -> macOS on large screens
 * - `windowsDesktop` -> any other OS on large screens
 * - `windowsMobile`  -> default
 *
 * An id can be used twice: once by a video and once by an image. The image is then the video's
 * poster, shown while the video loads. A video needs a poster for each of its sizes, otherwise
 * the tab has no poster.
 *
 * The screen size is left to the browser (media queries). Only the OS needs JS.
 * Tabs without a `windowsMobile` entry use their first asset (preferring a video), paired the same way.
 */
export function pickHeroTabsMedia(media: readonly GenericAsset[] | undefined, os: OS): TabMedia | undefined {
  const byId = (id: string) => {
    const matches = media?.filter(item => item.fields.id === id && hasFile(item))
    return matches?.find(isVideo) ?? matches?.[0]
  }
  const posterOf = (video: GenericAsset) =>
    isVideo(video) && video.fields.id
      ? media?.find(item => item.fields.id === video.fields.id && isImage(item) && hasFile(item))
      : undefined
  const withPoster = (tabMedia: Omit<TabMedia, 'poster'>): TabMedia => {
    const fallback = posterOf(tabMedia.fallback)
    const desktop = tabMedia.desktop ? posterOf(tabMedia.desktop) : undefined
    if (!fallback || (tabMedia.desktop && !desktop)) return tabMedia
    return {...tabMedia, poster: {fallback, desktop}}
  }

  const windowsMobile = byId('windowsMobile')

  if (!windowsMobile) {
    const first = media?.find(item => hasFile(item) && isVideo(item)) ?? media?.find(hasFile)
    return first ? withPoster({fallback: first}) : undefined
  }

  if (os === OS.iOS) return withPoster({fallback: byId('macosMobile') ?? windowsMobile})

  if (os === OS.macOS) {
    return withPoster({
      fallback: byId('macosMobile') ?? windowsMobile,
      desktop: byId('macosDesktop') ?? byId('windowsDesktop'),
    })
  }

  return withPoster({fallback: windowsMobile, desktop: byId('windowsDesktop')})
}
